import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";

const API_URL = "/api";
const ROWS_PER_PAGE = 10;

const emptyIngredient = {
  item_id: "",
  qty: "",
  unit: "Kg",
};

export default function RecipeManager() {
  const { user, branchId } = useAuth();

  const [recipes, setRecipes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [viewingRecipe, setViewingRecipe] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const getEmptyForm = () => ({
    product: "",
    product_item_id: "",
    baseQty: 1,
    baseUnit: "Kg",
    ingredients: [{ ...emptyIngredient }],
    branches: user?.role === "staff" && branchId ? [Number(branchId)] : [],
  });

  const [form, setForm] = useState(getEmptyForm());

  const fetchRecipes = async () => {
    const response = await fetch(`${API_URL}/recipes`, {
      credentials: "include",
    });

    const data = await response.json();

    if (data.status === "success") {
      setRecipes(data.data || []);
    }
  };

  const fetchInventory = async () => {
    const response = await fetch(`${API_URL}/inventory/items`, {
      credentials: "include",
    });

    const data = await response.json();

    if (data.status === "success") {
      setInventory(data.data || []);
    }
  };

  const fetchBranches = async () => {
    const response = await fetch(`${API_URL}/branches`, {
      credentials: "include",
    });

    const data = await response.json();

    if (data.status === "success") {
      setBranches(data.data || []);
    }
  };

  const refreshData = async () => {
    try {
      await Promise.all([fetchRecipes(), fetchInventory(), fetchBranches()]);
    } catch (error) {
      console.error("Could not load recipe data:", error);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const finishedItems = useMemo(
    () =>
      inventory.filter(
        (item) => String(item.type || "").toLowerCase() === "finished",
      ),
    [inventory],
  );

  const rawItems = useMemo(
    () =>
      inventory.filter(
        (item) => String(item.type || "").toLowerCase() === "raw",
      ),
    [inventory],
  );

  const filteredRecipes = useMemo(() => {
    const searchText = search.trim().toLowerCase();

    if (!searchText) return recipes;

    return recipes.filter((recipe) => {
      const branchNames = recipe.branches
        ?.map((branch) => branch.name)
        .join(" ")
        .toLowerCase();

      const ingredientNames = recipe.ingredients
        ?.map((ingredient) => ingredient.item)
        .join(" ")
        .toLowerCase();

      return (
        recipe.product?.toLowerCase().includes(searchText) ||
        branchNames?.includes(searchText) ||
        ingredientNames?.includes(searchText)
      );
    });
  }, [recipes, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecipes.length / ROWS_PER_PAGE),
  );

  const currentPage = Math.min(page, totalPages);

  const paginatedRecipes = filteredRecipes.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );

  const openCreateModal = () => {
    setEditingRecipe(null);
    setForm(getEmptyForm());
    setShowFormModal(true);
  };

  const openEditModal = (recipe) => {
    setEditingRecipe(recipe);

    setForm({
      product: recipe.product || "",
      product_item_id: recipe.product_item_id || "",
      baseQty: recipe.baseQty || 1,
      baseUnit: recipe.baseUnit || "Kg",
      ingredients: recipe.ingredients?.map((ingredient) => ({
        item_id: ingredient.item_id,
        qty: ingredient.qty,
        unit: ingredient.unit,
      })) || [{ ...emptyIngredient }],
      branches:
        user?.role === "staff" && branchId
          ? [Number(branchId)]
          : recipe.branches?.map((branch) => Number(branch.id)) || [],
    });

    setShowFormModal(true);
  };

  const openViewModal = async (recipeId) => {
    try {
      const response = await fetch(`${API_URL}/recipes/${recipeId}`, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not load recipe.");
        return;
      }

      setViewingRecipe(data.data);
    } catch (error) {
      console.error(error);
      alert("Could not load recipe.");
    }
  };

  const deleteRecipe = async (recipe) => {
    const confirmed = window.confirm(
      `Delete the recipe for "${recipe.product}"?`,
    );

    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/recipes/${recipe.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not delete recipe.");
        return;
      }

      await fetchRecipes();
      alert("Recipe deleted successfully.");
    } catch (error) {
      console.error(error);
      alert("Could not delete recipe.");
    }
  };

  const addIngredient = () => {
    setForm((previous) => ({
      ...previous,
      ingredients: [...previous.ingredients, { ...emptyIngredient }],
    }));
  };

  const updateIngredient = (index, field, value) => {
    setForm((previous) => ({
      ...previous,
      ingredients: previous.ingredients.map((ingredient, currentIndex) =>
        currentIndex === index ? { ...ingredient, [field]: value } : ingredient,
      ),
    }));
  };

  const removeIngredient = (index) => {
    if (form.ingredients.length === 1) return;

    setForm((previous) => ({
      ...previous,
      ingredients: previous.ingredients.filter(
        (_, currentIndex) => currentIndex !== index,
      ),
    }));
  };

  const saveRecipe = async () => {
    if (!form.product_item_id || !form.product) {
      alert("Select a finished product.");
      return;
    }

    if (!form.branches.length) {
      alert("Select at least one branch.");
      return;
    }

    if (Number(form.baseQty) <= 0) {
      alert("Base quantity must be greater than zero.");
      return;
    }

    if (
      !form.ingredients.length ||
      form.ingredients.some(
        (ingredient) => !ingredient.item_id || Number(ingredient.qty) <= 0,
      )
    ) {
      alert("Select every ingredient and enter a valid quantity.");
      return;
    }

    const payload = {
      product: form.product,
      product_item_id: Number(form.product_item_id),
      baseQty: Number(form.baseQty),
      baseUnit: form.baseUnit,
      branches: form.branches,
      ingredients: form.ingredients.map((ingredient) => ({
        item_id: Number(ingredient.item_id),
        qty: Number(ingredient.qty),
        unit: ingredient.unit,
      })),
    };

    const url = editingRecipe
      ? `${API_URL}/recipes/${editingRecipe.id}`
      : `${API_URL}/recipes`;

    const method = editingRecipe ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not save recipe.");
        return;
      }

      alert(
        editingRecipe
          ? "Recipe updated successfully."
          : "Recipe created successfully.",
      );

      setShowFormModal(false);
      setEditingRecipe(null);
      setForm(getEmptyForm());
      await fetchRecipes();
    } catch (error) {
      console.error(error);
      alert("Could not save recipe.");
    }
  };

  const downloadTemplate = () => {
    window.open(
      `${API_URL}/recipes/import-template`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const importRecipes = async () => {
    if (!importFile) {
      alert("Choose an Excel file first.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await fetch(`${API_URL}/recipes/import-excel`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      setImportResult(data);

      if (data.status === "success") {
        await fetchRecipes();
      }
    } catch (error) {
      console.error(error);
      setImportResult({
        status: "error",
        message: "Could not upload the Excel file.",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h1 className="text-2xl font-serif">Production Recipes (BOM)</h1>
            <p className="mt-1 text-sm text-gray-400">
              Manage finished products and raw-material requirements.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="rounded-xl border border-amber-400/50 px-5 py-2 font-medium text-amber-300 hover:bg-amber-400/10"
            >
              Import Excel
            </button>

            <button
              onClick={openCreateModal}
              className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black hover:bg-amber-300"
            >
              + Create Recipe
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:flex-row">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search product, ingredient, or branch..."
            className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
          />

          <button
            onClick={() => setSearch("")}
            className="rounded-lg border border-white/20 px-4 py-2 text-sm text-gray-300 hover:bg-white/5"
          >
            Clear
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-gray-400">
                <tr>
                  <th className="px-5 py-4 text-left">Product</th>
                  <th className="px-5 py-4 text-left">Base Quantity</th>
                  <th className="px-5 py-4 text-left">Ingredients</th>
                  <th className="px-5 py-4 text-left">Branches</th>
                  <th className="px-5 py-4 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginatedRecipes.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No recipes found.
                    </td>
                  </tr>
                ) : (
                  paginatedRecipes.map((recipe) => (
                    <tr
                      key={recipe.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-5 py-4 font-medium">
                        {recipe.product}
                      </td>

                      <td className="px-5 py-4">
                        {recipe.baseQty} {recipe.baseUnit}
                      </td>

                      <td className="px-5 py-4 text-gray-300">
                        {recipe.ingredients?.length || 0} item(s)
                      </td>

                      <td className="px-5 py-4 text-gray-400">
                        {recipe.branches
                          ?.map((branch) => branch.name)
                          .join(", ") || "-"}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => openViewModal(recipe.id)}
                            className="text-sky-300 hover:underline"
                          >
                            View
                          </button>

                          <button
                            onClick={() => openEditModal(recipe)}
                            className="text-amber-300 hover:underline"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => deleteRecipe(recipe)}
                            className="text-red-400 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-3 border-t border-white/10 bg-black/20 px-5 py-3 text-sm md:flex-row md:items-center md:justify-between">
            <p className="text-gray-400">
              Showing{" "}
              {filteredRecipes.length
                ? (currentPage - 1) * ROWS_PER_PAGE + 1
                : 0}
              {" - "}
              {Math.min(
                currentPage * ROWS_PER_PAGE,
                filteredRecipes.length,
              )} of {filteredRecipes.length} recipes
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>

              <span className="px-2 text-gray-300">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setPage((previous) => Math.min(totalPages, previous + 1))
                }
                disabled={currentPage === totalPages}
                className="rounded-lg border border-white/15 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {showFormModal && (
          <RecipeFormModal
            form={form}
            setForm={setForm}
            branches={branches}
            branchId={branchId}
            user={user}
            finishedItems={finishedItems}
            rawItems={rawItems}
            editingRecipe={editingRecipe}
            onAddIngredient={addIngredient}
            onUpdateIngredient={updateIngredient}
            onRemoveIngredient={removeIngredient}
            onClose={() => {
              setShowFormModal(false);
              setEditingRecipe(null);
            }}
            onSave={saveRecipe}
          />
        )}

        {showImportModal && (
          <ImportRecipeModal
            importFile={importFile}
            importing={importing}
            importResult={importResult}
            onFileChange={(file) => {
              setImportFile(file);
              setImportResult(null);
            }}
            onClose={() => setShowImportModal(false)}
            onDownloadTemplate={downloadTemplate}
            onImport={importRecipes}
          />
        )}

        {viewingRecipe && (
          <ViewRecipeModal
            recipe={viewingRecipe}
            onClose={() => setViewingRecipe(null)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function RecipeFormModal({
  form,
  setForm,
  branches,
  branchId,
  user,
  finishedItems,
  rawItems,
  editingRecipe,
  onAddIngredient,
  onUpdateIngredient,
  onRemoveIngredient,
  onClose,
  onSave,
}) {
  return (
    <Modal onClose={onClose}>
      <h2 className="mb-6 text-xl font-serif">
        {editingRecipe ? "Edit Recipe (BOM)" : "Create Recipe (BOM)"}
      </h2>

      <select
        value={form.product_item_id}
        onChange={(event) => {
          const id = Number(event.target.value);
          const item = finishedItems.find(
            (inventoryItem) => inventoryItem.id === id,
          );

          setForm((previous) => ({
            ...previous,
            product_item_id: id,
            product: item?.name || "",
          }));
        }}
        className="mb-4 w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
      >
        <option value="">Select Finished Product</option>

        {finishedItems.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </select>

      {user?.role === "staff" ? (
        <div className="mb-4 rounded-lg border border-white/10 bg-black/40 px-4 py-2 text-sm text-gray-300">
          Assigned Branch:{" "}
          {branches.find((branch) => branch.id === Number(branchId))?.name ||
            "Loading..."}
        </div>
      ) : (
        <select
          multiple
          value={form.branches || []}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              branches: Array.from(event.target.selectedOptions, (option) =>
                Number(option.value),
              ),
            }))
          }
          className="mb-4 w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </select>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        <input
          type="number"
          min="0.001"
          step="0.001"
          placeholder="Base Quantity"
          value={form.baseQty}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              baseQty: event.target.value,
            }))
          }
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        />

        <select
          value={form.baseUnit}
          onChange={(event) =>
            setForm((previous) => ({
              ...previous,
              baseUnit: event.target.value,
            }))
          }
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          <option value="Kg">Kg</option>
          <option value="Pieces">Pieces</option>
          <option value="Litres">Litres</option>
        </select>
      </div>

      <p className="mb-3 text-xs uppercase tracking-widest text-gray-400">
        Raw Ingredients
      </p>

      <div className="max-h-[45vh] space-y-3 overflow-y-auto">
        {form.ingredients.map((ingredient, index) => (
          <div key={index} className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <select
              value={ingredient.item_id || ""}
              onChange={(event) =>
                onUpdateIngredient(index, "item_id", Number(event.target.value))
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="">Select Raw Ingredient</option>

              {rawItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            <input
              type="number"
              min="0.001"
              step="0.001"
              placeholder="Quantity"
              value={ingredient.qty}
              onChange={(event) =>
                onUpdateIngredient(index, "qty", event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            />

            <select
              value={ingredient.unit}
              onChange={(event) =>
                onUpdateIngredient(index, "unit", event.target.value)
              }
              className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
            >
              <option value="Kg">Kg</option>
              <option value="Litres">Litres</option>
              <option value="Pieces">Pieces</option>
              <option value="Grams">Grams</option>
            </select>

            <button
              onClick={() => onRemoveIngredient(index)}
              disabled={form.ingredients.length === 1}
              className="rounded-lg px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={onAddIngredient}
        className="mt-4 text-sm text-amber-400 hover:underline"
      >
        + Add Ingredient
      </button>

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={onClose}
          className="rounded-xl border border-white/20 px-6 py-2"
        >
          Cancel
        </button>

        <button
          onClick={onSave}
          className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black"
        >
          {editingRecipe ? "Save Changes" : "Save Recipe"}
        </button>
      </div>
    </Modal>
  );
}

function ViewRecipeModal({ recipe, onClose }) {
  return (
    <Modal onClose={onClose}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-serif">{recipe.product}</h2>
          <p className="mt-1 text-sm text-gray-400">
            Recipe details and ingredient quantities.
          </p>
        </div>

        <span className="rounded-full bg-amber-400/10 px-3 py-1 text-sm text-amber-300">
          Per {recipe.baseQty} {recipe.baseUnit}
        </span>
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
        <p className="text-xs uppercase tracking-widest text-gray-400">
          Available Branches
        </p>

        <p className="mt-2 text-sm">
          {recipe.branches?.map((branch) => branch.name).join(", ") || "-"}
        </p>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">Raw Ingredient</th>
              <th className="px-4 py-3 text-right">Quantity</th>
              <th className="px-4 py-3 text-left">Unit</th>
            </tr>
          </thead>

          <tbody>
            {recipe.ingredients?.map((ingredient, index) => (
              <tr
                key={`${ingredient.item_id}-${index}`}
                className="border-t border-white/10"
              >
                <td className="px-4 py-3">{ingredient.item}</td>
                <td className="px-4 py-3 text-right text-amber-300">
                  {ingredient.qty}
                </td>
                <td className="px-4 py-3">{ingredient.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          onClick={onClose}
          className="rounded-xl border border-white/20 px-6 py-2"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

function ImportRecipeModal({
  importFile,
  importing,
  importResult,
  onFileChange,
  onClose,
  onDownloadTemplate,
  onImport,
}) {
  return (
    <Modal onClose={onClose}>
      <h2 className="text-xl font-serif">Import Recipes from Excel</h2>

      <p className="mt-2 text-sm text-gray-400">
        Repeat the product name on multiple rows to add multiple ingredients to
        one recipe.
      </p>

      <button
        onClick={onDownloadTemplate}
        className="mt-5 rounded-xl border border-amber-400/50 px-4 py-2 text-sm text-amber-300 hover:bg-amber-400/10"
      >
        Download Excel Template
      </button>

      <input
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(event) => onFileChange(event.target.files?.[0] || null)}
        className="mt-5 block w-full rounded-lg border border-white/10 bg-black/40 p-3 text-sm"
      />

      {importFile && (
        <p className="mt-3 text-sm text-gray-300">
          Selected: {importFile.name}
        </p>
      )}

      {importResult && (
        <div
          className={`mt-5 rounded-xl border p-4 text-sm ${
            importResult.status === "success"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : "border-red-500/30 bg-red-500/10 text-red-200"
          }`}
        >
          <p className="font-medium">{importResult.message}</p>

          {importResult.status === "success" && (
            <p className="mt-2">
              Created: {importResult.created || 0} · Skipped:{" "}
              {importResult.skipped || 0}
            </p>
          )}

          {importResult.errors?.map((error, index) => (
            <p key={index} className="mt-2">
              Row {error.row}: {error.message}
            </p>
          ))}
        </div>
      )}

      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={onClose}
          disabled={importing}
          className="rounded-xl border border-white/20 px-6 py-2"
        >
          Close
        </button>

        <button
          onClick={onImport}
          disabled={!importFile || importing}
          className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black disabled:cursor-not-allowed disabled:opacity-50"
        >
          {importing ? "Importing..." : "Import Recipes"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0b0f] p-8">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>

        {children}
      </div>
    </div>
  );
}
